import React from "react";
import { useTranslation } from "react-i18next";
import { Box, Button, Card, Skeleton, Typography } from "@mui/material";
import { AutoGrid, Center, Stack } from "components/layout/reusableLayouts";
import ErrorState from "components/common/ErrorState";
import { AstradaConnection } from "./types";
import ConnectionCard from "./ConnectionCard";
import { CreditCardIcon } from "@hugeicons-pro/core-stroke-rounded";
import { HugeiconsIcon } from "@hugeicons/react";

export interface ConnectionsSectionProps {
  connections: AstradaConnection[];
  loading: boolean;
  /** The connections query failed. With nothing cached, `connections` is empty
   *  because of the failure, and the empty state would offer to enroll a
   *  program the company may already have. */
  error?: boolean;
  isDisabledEnroll?: boolean;
  onRetry?: () => void;
  onManage: (connectionId: string) => void;
  onActive: (connection: AstradaConnection) => void;
  onEnroll: () => void;
}

/** The company's enrolled card programs, the empty state, or a load failure. */
const ConnectionsSection: React.FC<ConnectionsSectionProps> = ({
  connections,
  loading,
  error = false,
  onRetry,
  onManage,
  onActive,
  onEnroll,
  isDisabledEnroll,
}) => {
  const { t } = useTranslation();

  return (
    <Stack gap="var(--space-4)">
      {loading && connections.length === 0 ? (
        <Skeleton variant="rounded" height={140} />
      ) : error && connections.length === 0 ? (
        <Card variant="outlined" sx={{ minWidth: 0 }}>
          <ErrorState
            title={t("Something went wrong")}
            description={t("LinkedAccountsLoadError")}
            retryLabel={t("Retry")}
            onRetry={onRetry}
          />
        </Card>
      ) : connections.length === 0 ? (
        <Card
          variant="outlined"
          sx={{
            display: "flex",
            flexDirection: "column",
            minWidth: 0,
            padding: "var(--space-5)",
            minHeight: "300px",
            backgroundColor: "var(--color-bg-elevated)",
            justifyContent: "center",
          }}
        >
          <Center maxWidth={420}>
            <Stack
              gap="var(--space-3)"
              sx={{ textAlign: "center", alignItems: "center" }}
            >
              <Box
                sx={{
                  backgroundColor: "var(--color-bg-brand-subtle)",
                  borderRadius: "var(--radius-full)",
                  padding: "var(--space-4)",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <HugeiconsIcon
                  icon={CreditCardIcon}
                  size={20}
                  color="var(--color-text-tertiary)"
                />
              </Box>
              <Typography variant="h5">{t("NoLinkedAccountsTitle")}</Typography>
              <Typography
                variant="body1"
                sx={{ color: "var(--color-text-tertiary)" }}
              >
                {t("NoLinkedAccountsMessage")}
              </Typography>
              {/* With no connections the page header has no button, so this is
                  the only way into the enroll flow. */}
              <Button
                variant="primary-filled"
                onClick={onEnroll}
                disabled={isDisabledEnroll}
              >
                {t("Enroll program")}
              </Button>
            </Stack>
          </Center>
        </Card>
      ) : (
        <Stack gap="var(--space-3)">
          <Typography variant="h6">{t("Connections")}</Typography>
          <AutoGrid min="320px">
            {connections.map((connection) => (
              <ConnectionCard
                key={connection.id}
                connection={connection}
                onManage={onManage}
                onActive={onActive}
              />
            ))}
          </AutoGrid>
        </Stack>
      )}
    </Stack>
  );
};

export default ConnectionsSection;
